<form [formGroup]="formGroup" class="flex flex-column gap-4">
  <!-- Logo Upload Section -->
  <div class="flex flex-column gap-2">
    <label for="logo" class="font-medium">Logo de la banque <span class="text-red-500">*</span></label>
    <div class="flex align-items-start gap-3 p-3 border-1 surface-border border-round">
      <!-- Upload Info & Button -->
      <div class="flex-1">
        <!-- File Upload for Create Mode -->
        <div *ngIf="boundData?.isCreate">
          <p-fileUpload
            class="custom-file-upload-compact"
            (onSelect)="onUpload($event)"
            [showUploadButton]="false"
            [showCancelButton]="false"
            [multiple]="false"
            accept="image/*"
            [maxFileSize]="maxFileSize"
            chooseLabel="Choisir un fichier"
            chooseIcon="pi pi-upload"
            styleClass="p-button-sm">
          </p-fileUpload>

          <div *ngIf="uploadedFiles.length > 0" class="mt-2">
            <small class="text-green-600">
              <i class="pi pi-check-circle mr-1"></i>
              <strong>{{ uploadedFiles[0].name }}</strong> ({{ (uploadedFiles[0].size / 1024).toFixed(0) }} KB)
            </small>
          </div>
        </div>

        <!-- File Upload for Edit Mode -->
        <div *ngIf="boundData?.isEdit">
          <p-fileUpload
            mode="basic"
            chooseLabel="Modifier le logo"
            chooseIcon="pi pi-upload"
            [multiple]="false"
            accept="image/*"
            [maxFileSize]="maxFileSize"
            (onSelect)="onLogoUpdate($event)"
            styleClass="p-button-outlined p-button-sm" />
        </div>
      </div>

      <!-- Preview Avatar -->
      <div class="logo-preview-container">
        <p-avatar
          [image]="logoPreview || boundData?.data?.logoUrl || 'assets/default-bank.png'"
          styleClass="org-avatar shadow-2"
          [style]="{ 'width': '80px', 'height': '80px', 'border': '2px solid var(--surface-border)' }"
          shape="circle" />
        <div *ngIf="uploadedFiles.length > 0" class="logo-badge">
          <i class="pi pi-check text-white"></i>
        </div>
      </div>
    </div>
    <small *ngIf="!fileError" class="text-500">
      JPG, PNG, GIF, WEBP • Max {{ maxFileSize / 1000000 }}MB
    </small>
    <small *ngIf="fileError" class="text-red-600">
      <i class="pi pi-exclamation-triangle mr-1"></i>{{ fileError }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="name" class="font-medium">Nom <span class="text-red-500">*</span></label>
    <input id="name" type="text" pInputText formControlName="name"
           [ngClass]="{'ng-invalid ng-dirty': hasFieldError('name')}" />
    <small *ngIf="!hasFieldError('name')" class="text-gray-600">
      Nom de la banque (2-100 caractères).
    </small>
    <small *ngIf="hasFieldError('name')" class="text-red-500">
      {{ getFieldError('name') }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="swiftCode" class="font-medium">SWIFT Code - BIC <span class="text-red-500">*</span></label>
    <input id="swiftCode" type="text" pInputText formControlName="swiftCode"
           [ngClass]="{'ng-invalid ng-dirty': hasFieldError('swiftCode')}"
           placeholder="BNPAFRPP ou BNPAFRPPXXX" />
    <small *ngIf="!hasFieldError('swiftCode')" class="text-gray-600">
      Code SWIFT/BIC (8 ou 11 caractères).
    </small>
    <small *ngIf="hasFieldError('swiftCode')" class="text-red-500">
      {{ getFieldError('swiftCode') }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="codeBank" class="font-medium">Code banque <span class="text-red-500">*</span></label>
    <input id="codeBank" type="text" pInputText formControlName="codeBank"
           [ngClass]="{'ng-invalid ng-dirty': hasFieldError('codeBank')}" />
    <small *ngIf="!hasFieldError('codeBank')" class="text-gray-600">
      Code banque (3-10 caractères).
    </small>
    <small *ngIf="hasFieldError('codeBank')" class="text-red-500">
      {{ getFieldError('codeBank') }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="telephone" class="font-medium">Téléphone <span class="text-red-500">*</span></label>
    <input id="telephone" type="text" pInputText formControlName="telephone"
           [ngClass]="{'ng-invalid ng-dirty': hasFieldError('telephone')}"
           placeholder="+221 33 956 04 04" />
    <small *ngIf="!hasFieldError('telephone')" class="text-gray-600">
      Numéro de téléphone (8-15 chiffres).
    </small>
    <small *ngIf="hasFieldError('telephone')" class="text-red-500">
      {{ getFieldError('telephone') }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="email" class="font-medium">Email <span class="text-red-500">*</span></label>
    <input id="email" type="email" pInputText formControlName="email"
           [ngClass]="{'ng-invalid ng-dirty': hasFieldError('email')}"
           placeholder="contact@banque.com" />
    <small *ngIf="!hasFieldError('email')" class="text-gray-600">
      Adresse email de la banque.
    </small>
    <small *ngIf="hasFieldError('email')" class="text-red-500">
      {{ getFieldError('email') }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="country" class="font-medium">Pays <span class="text-red-500">*</span></label>
    <p-select id="country" [options]="countries" formControlName="country"
                placeholder="Sélectionner un pays" [filter]="true" filterBy="country"
                optionLabel="country" optionValue="country"
                [ngClass]="{'ng-invalid ng-dirty': hasFieldError('country')}" />
    <small *ngIf="!hasFieldError('country')" class="text-gray-600">
      Pays de la banque.
    </small>
    <small *ngIf="hasFieldError('country')" class="text-red-500">
      {{ getFieldError('country') }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="address" class="font-medium">Adresse <span class="text-red-500">*</span></label>
    <input id="address" type="text" pInputText formControlName="address"
           [ngClass]="{'ng-invalid ng-dirty': hasFieldError('address')}" />
    <small *ngIf="!hasFieldError('address')" class="text-gray-600">
      Adresse de la banque (max 200 caractères).
    </small>
    <small *ngIf="hasFieldError('address')" class="text-red-500">
      {{ getFieldError('address') }}
    </small>
  </div>

  <div class="flex flex-column gap-2">
    <label for="description" class="font-medium">Description</label>
    <textarea pTextarea id="description" rows="5" cols="30" formControlName="description"
              [ngClass]="{'ng-invalid ng-dirty': hasFieldError('description')}"
              placeholder="Description de la banque..."></textarea>
    <small *ngIf="!hasFieldError('description')" class="text-gray-600">
      Description de la banque (max 500 caractères).
    </small>
    <small *ngIf="hasFieldError('description')" class="text-red-500">
      {{ getFieldError('description') }}
    </small>
  </div>
</form>
<div class="flex justify-content-end mt-4">
  <p-button *ngIf="boundData?.isCreate" (onClick)="create()" [loading]="isLoading" label="Enregistrer" />
  <p-button *ngIf="boundData?.isEdit" (onClick)="update()" [loading]="isLoading" label="Enregistrer" />
</div>
